/**
 * PerfilFormDialog - Crear/Editar perfil de examen
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Search, X, TestTubes } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { useApiError } from "@/hooks/use-api-error";
import {
  usePerfil,
  useCreatePerfil,
  useUpdatePerfil,
  useBuscarExamenesTipo,
  type ExamenTipoListItem,
} from "@/hooks/use-ordenes";
import { useDebounce } from "@/hooks/use-debounce";

const formSchema = z.object({
  codigo: z.string().min(1, "Requerido").max(20),
  nombre: z.string().min(1, "Requerido").max(200),
  precio: z.coerce.number().min(0).default(0),
  descripcion: z.string().default(""),
});

type FormValues = z.infer<typeof formSchema>;

interface PerfilFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  perfilId: number | null;
  onSuccess: (isNew: boolean) => void;
}

export function PerfilFormDialog({
  open,
  onOpenChange,
  perfilId,
  onSuccess,
}: PerfilFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isNew = perfilId === null;

  const { data: perfil } = usePerfil(perfilId || 0, { enabled: !!perfilId && open });

  const [selectedExamenes, setSelectedExamenes] = useState<ExamenTipoListItem[]>([]);
  const [examenSearch, setExamenSearch] = useState("");
  const debouncedSearch = useDebounce(examenSearch, 300);

  const { data: examenesResults } = useBuscarExamenesTipo(debouncedSearch, {
    enabled: debouncedSearch.length >= 2,
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { codigo: "", nombre: "", precio: 0, descripcion: "" },
  });

  useEffect(() => {
    if (perfil && !isNew) {
      form.reset({
        codigo: perfil.codigo,
        nombre: perfil.nombre,
        precio: Number(perfil.precio),
        descripcion: perfil.descripcion || "",
      });
      setSelectedExamenes(perfil.examenes || []);
    } else if (isNew && open) {
      form.reset();
      setSelectedExamenes([]);
    }
  }, [perfil, isNew, open, form]);

  const handleAddExamen = useCallback((examen: ExamenTipoListItem) => {
    setSelectedExamenes((prev) => {
      if (prev.some((e) => e.id === examen.id)) return prev;
      return [...prev, examen];
    });
    setExamenSearch("");
  }, []);

  const handleRemoveExamen = useCallback((id: number) => {
    setSelectedExamenes((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const createMutation = useCreatePerfil({
    onSuccess: () => onSuccess(true),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const updateMutation = useUpdatePerfil({
    onSuccess: () => onSuccess(false),
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const onSubmit = (values: FormValues) => {
    const data = { ...values, examenes: selectedExamenes.map((e) => e.id) };
    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate({ id: perfilId!, data });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const filteredResults = examenesResults?.filter(
    (e) => !selectedExamenes.some((s) => s.id === e.id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNew ? t("ordenes.perfiles.newProfile") : t("ordenes.perfiles.editProfile")}
          </DialogTitle>
          <DialogDescription>
            {isNew ? t("ordenes.perfiles.newProfileDesc") : t("ordenes.perfiles.editProfileDesc")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="codigo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.perfiles.fields.code")} *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="precio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("ordenes.perfiles.fields.price")}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        {...field}
                        value={field.value?.toString() || ""}
                        onChange={(e) =>
                          field.onChange(e.target.value ? Number(e.target.value) : 0)
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="nombre"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.perfiles.fields.name")} *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="descripcion"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("ordenes.perfiles.fields.description")}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Exámenes selector */}
            <div className="space-y-2">
              <FormLabel>{t("ordenes.perfiles.fields.exams")} *</FormLabel>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t("ordenes.searchExams")}
                  value={examenSearch}
                  onChange={(e) => setExamenSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {filteredResults && filteredResults.length > 0 && (
                <div className="border rounded-md max-h-32 overflow-y-auto">
                  {filteredResults.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                      onClick={() => handleAddExamen(e)}
                    >
                      <span>
                        <span className="font-mono text-xs text-muted-foreground mr-2">
                          {e.codigo}
                        </span>
                        {e.nombre}
                      </span>
                      <span className="text-muted-foreground">L {Number(e.precio).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedExamenes.length > 0 && (
                <ScrollArea className="max-h-40">
                  <div className="space-y-1">
                    {selectedExamenes.map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center justify-between gap-2 p-2 border rounded-md text-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <TestTubes className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="font-mono text-xs text-muted-foreground">
                            {e.codigo}
                          </span>
                          <span className="truncate">{e.nombre}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={() => handleRemoveExamen(e.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isPending || selectedExamenes.length === 0}>
                {isPending ? t("common.saving") : t("common.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
